<template>
  <div class="row">
    <!-- Filtres et recherche -->
    <div class="col-12 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div
          class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Mes Dossiers</h4>
          <div class="d-flex gap-2">
            <button @click="loadDossiers" class="btn btn-outline-primary btn-sm">
              <i class="flaticon-refresh me-1"></i> Actualiser
            </button>
            <router-link to="/ajouter/etape-1" class="btn btn-primary btn-sm">
              <i class="flaticon-plus me-1"></i> Nouveau dossier
            </router-link>
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="row mb-3">
            <div class="col-md-4">
              <div class="form-group">
                <label class="form-label">Rechercher</label>
                <input v-model="filters.search" @input="applyFilters" type="text" class="form-control"
                  placeholder="Référence, client, destination...">
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label class="form-label">Statut</label>
                <Multiselect v-model="filters.status" :options="statusOptions" :searchable="false"
                  :close-on-select="true" placeholder="Tous les statuts" @change="applyFilters" />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label class="form-label">Date de création</label>
                <input v-model="filters.dateFrom" @change="applyFilters" type="date" class="form-control">
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label class="form-label">&nbsp;</label>
                <button @click="clearFilters" class="btn btn-outline-secondary w-100">
                  <i class="flaticon-close me-1"></i> Effacer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Liste des dossiers -->
    <div class="col-12">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <div v-else-if="filteredDossiers.length === 0" class="text-center py-4">
            <i class="flaticon-folder-open text-muted" style="font-size: 48px;"></i>
            <p class="text-muted mt-2">Aucun dossier trouvé</p>
          </div>

          <div v-else>
            <!-- Vue en cartes pour mobile -->
            <div class="d-md-none">
              <div v-for="dossier in paginatedDossiers" :key="dossier.id" class="dossier-card mb-3">
                <div class="card border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <h6 class="fw-bold mb-0">{{ dossier.refbesc }}</h6>
                      <span v-html="getStatusBadge(dossier.status)"></span>
                    </div>
                    <p class="text-muted small mb-1">
                      <i class="flaticon-user me-1"></i> {{ dossier.client }}
                    </p>
                    <p class="text-muted small mb-1">
                      <i class="flaticon-location me-1"></i> {{ dossier.destination }}
                    </p>
                    <p class="text-muted small mb-2">
                      <i class="flaticon-calendar me-1"></i> {{ format_date(dossier.created_at) }}
                    </p>
                    <div class="d-flex gap-2">
                      <router-link :to="{ name: 'ViewBesc', params: { id: dossier.id } }"
                        class="btn btn-primary btn-sm flex-fill">
                        <i class="flaticon-eye me-1"></i> Voir
                      </router-link>
                      <button v-if="dossier.status === 'BROUILLON'" @click="editDossier(dossier.id)"
                        class="btn btn-outline-primary btn-sm flex-fill">
                        <i class="flaticon-edit me-1"></i> Modifier
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vue en tableau pour desktop -->
            <div class="d-none d-md-block">
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Référence BESC</th>
                      <th>Client</th>
                      <th>Destination</th>
                      <th>Type de transport</th>
                      <th>Statut</th>
                      <th>Date création</th>
                      <th>Dernière modification</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dossier in paginatedDossiers" :key="dossier.id">
                      <td class="fw-medium">{{ dossier.refbesc }}</td>
                      <td>{{ dossier.client }}</td>
                      <td>{{ dossier.destination }}</td>
                      <td>
                        <span class="badge bg-info">{{ dossier.transport_type }}</span>
                      </td>
                      <td>
                        <span v-html="getStatusBadge(dossier.status)"></span>
                      </td>
                      <td>{{ format_date(dossier.created_at) }}</td>
                      <td>{{ format_date(dossier.updated_at) }}</td>
                      <td class="text-end">
                        <div class="dropdown">
                          <span class="badge text-white bg-primary fs-15 dropdown-toggle" data-bs-toggle="dropdown"
                            aria-expanded="false" style="cursor: pointer;">
                            Actions
                            <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                          </span>
                          <ul class="dropdown-menu">
                            <li>
                              <router-link :to="{ name: 'ViewBesc', params: { id: dossier.id } }"
                                class="dropdown-item d-flex align-items-center">
                                <i class="flaticon-eye lh-1 me-8 position-relative top-1"></i>
                                Détails
                              </router-link>
                            </li>
                            <li v-if="dossier.status === 'BROUILLON'">
                              <a href="javascript:void(0);" @click="editDossier(dossier.id)"
                                class="dropdown-item d-flex align-items-center">
                                <i class="flaticon-edit lh-1 me-8 position-relative top-1"></i>
                                Modifier
                              </a>
                            </li>
                            <li v-if="dossier.status === 'BROUILLON'">
                              <a href="javascript:void(0);" @click="telechargerBrouillon(dossier.id)"
                                class="dropdown-item d-flex align-items-center">
                                <i class="flaticon-file-1 lh-1 me-8 position-relative top-1"></i>
                                Télécharger brouillon
                              </a>
                            </li>
                            <li v-if="dossier.status === 'VALIDE'">
                              <a href="javascript:void(0);" @click="telechargerPDF(dossier.id)"
                                class="dropdown-item d-flex align-items-center">
                                <i class="flaticon-file-1 lh-1 me-8 position-relative top-1"></i>
                                Télécharger PDF
                              </a>
                            </li>
                            <li v-if="canDeleteDossier(dossier.status)">
                              <hr class="dropdown-divider">
                            </li>
                            <li v-if="canDeleteDossier(dossier.status)">
                              <a href="javascript:void(0);" @click="confirmDelete(dossier)"
                                class="dropdown-item d-flex align-items-center text-danger">
                                <i class="flaticon-delete lh-1 me-8 position-relative top-1"></i>
                                Supprimer
                              </a>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
              <div class="text-muted">
                Affichage {{ (currentPage - 1) * itemsPerPage + 1 }} à
                {{ Math.min(currentPage * itemsPerPage, filteredDossiers.length) }}
                sur {{ filteredDossiers.length }} dossiers
              </div>
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button @click="currentPage = 1" class="page-link">Première</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button @click="currentPage--" class="page-link">Précédent</button>
                  </li>
                  <li v-for="page in visiblePages" :key="page" class="page-item"
                    :class="{ active: page === currentPage }">
                    <button @click="currentPage = page" class="page-link">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <button @click="currentPage++" class="page-link">Suivant</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <button @click="currentPage = totalPages" class="page-link">Dernière</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from 'vue';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import { error, format_date, success } from '@/utils/utils';
import { useRouter } from 'vue-router';
import Multiselect from '@vueform/multiselect';

interface Dossier {
  id: string;
  refbesc: string;
  client: string;
  destination: string;
  transport_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Filters {
  search: string;
  status: string;
  dateFrom: string;
}

export default defineComponent({
  name: 'TransitaireDossiers',
  components: {
    Multiselect
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const dossiers = ref<Dossier[]>([]);
    const currentPage = ref(1);
    const itemsPerPage = ref(10);

    const filters = ref<Filters>({
      search: '',
      status: '',
      dateFrom: ''
    });

    const statusOptions = [
      { value: '', label: 'Tous les statuts' },
      { value: 'BROUILLON', label: 'Brouillon' },
      { value: 'SOUMIS', label: 'Soumis' },
      { value: 'EN_COURS', label: 'En cours' },
      { value: 'VALIDE', label: 'Validé' },
      { value: 'REJETE', label: 'Rejeté' }
    ];

    const loadDossiers = async () => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const response = await ApiService.get(`/transitaire/dossiers/${user.userId}`);
        dossiers.value = response.data.data;

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des dossiers');
      } finally {
        loading.value = false;
      }
    };

    const filteredDossiers = computed(() => {
      let filtered = [...dossiers.value];

      if (filters.value.search) {
        const search = filters.value.search.toLowerCase();
        filtered = filtered.filter(dossier =>
          dossier.refbesc.toLowerCase().includes(search) ||
          dossier.client.toLowerCase().includes(search) ||
          dossier.destination.toLowerCase().includes(search)
        );
      }

      if (filters.value.status) {
        filtered = filtered.filter(dossier => dossier.status === filters.value.status);
      }

      if (filters.value.dateFrom) {
        const fromDate = new Date(filters.value.dateFrom);
        filtered = filtered.filter(dossier =>
          new Date(dossier.created_at) >= fromDate
        );
      }

      return filtered;
    });

    const totalPages = computed(() =>
      Math.ceil(filteredDossiers.value.length / itemsPerPage.value)
    );

    const paginatedDossiers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return filteredDossiers.value.slice(start, end);
    });

    const visiblePages = computed<number[]>(() => {
      const pages: number[] = [];
      const start = Math.max(1, currentPage.value - 2);
      const end = Math.min(totalPages.value, currentPage.value + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    });


    const applyFilters = () => {
      currentPage.value = 1;
    };

    const clearFilters = () => {
      filters.value = {
        search: '',
        status: '',
        dateFrom: ''
      };
      currentPage.value = 1;
    };

    const getStatusBadge = (status: string): string => {
      const badges = {
        'BROUILLON': '<span class="badge bg-secondary">Brouillon</span>',
        'SOUMIS': '<span class="badge bg-primary">Soumis</span>',
        'EN_COURS': '<span class="badge bg-info">En cours</span>',
        'VALIDE': '<span class="badge bg-success">Validé</span>',
        'REJETE': '<span class="badge bg-danger">Rejeté</span>'
      };
      return badges[status] || `<span class="badge bg-secondary">${status}</span>`;
    };

    const editDossier = (dossierId: string) => {
      router.push({ name: 'EditBesc', params: { id: dossierId } });
    };

    const canDeleteDossier = (status: string): boolean => {
      return status === 'BROUILLON';
    };

    const confirmDelete = (dossier: Dossier) => {
      if (confirm(`Êtes-vous sûr de vouloir supprimer le dossier ${dossier.refbesc} ?`)) {
        deleteDossier(dossier.id);
      }
    };

    const deleteDossier = async (dossierId: string) => {
      try {
        await ApiService.delete(`/besc/${dossierId}`);
        success('Dossier supprimé avec succès');
        loadDossiers();
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la suppression');
      }
    };

    const telechargerBrouillon = async (id: string) => {
      try {
        const response = await ApiService.getFile(`/besc/${id}/download-draft`);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `BESC_Brouillon_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        success('Brouillon téléchargé avec succès');
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du téléchargement du brouillon');
      }
    };

    const telechargerPDF = async (id: string) => {
      try {
        const response = await ApiService.getFile(`/besc/${id}/download-pdf`);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `BESC_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        success('PDF téléchargé avec succès');
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du téléchargement du PDF');
      }
    };

    onMounted(() => {
      loadDossiers();
    });

    return {
      loading,
      dossiers,
      filteredDossiers,
      paginatedDossiers,
      currentPage,
      totalPages,
      visiblePages,
      itemsPerPage,
      filters,
      statusOptions,
      loadDossiers,
      applyFilters,
      clearFilters,
      getStatusBadge,
      format_date,
      editDossier,
      canDeleteDossier,
      confirmDelete,
      telechargerBrouillon,
      telechargerPDF
    };
  }
});
</script>

<style scoped>
.dossier-card {
  transition: all 0.3s ease;
}

.dossier-card:hover {
  transform: translateY(-2px);
}

.pagination .page-link {
  border-color: #dee2e6;
  color: #6c757d;
}

.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.pagination .page-link:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}
</style>
